// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

axios.defaults.withCredentials = true; // ensure cookies (entry gate) are sent with auth requests

// Define base API URL - use the same one your app is already using
const API_URL = process.env.REACT_APP_API_URL || 'https://uio-poreznik-production.up.railway.app/api';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken')); // Initialize token from localStorage
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to set token in state, localStorage, and axios headers
  const handleSetToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem('authToken', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    } else {
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
    }
  }, []);

  // Fetch current user data - memoized with useCallback
  const fetchCurrentUser = useCallback(async (currentToken) => {
    if (!currentToken) {
        setLoading(false);
        setCurrentUser(null);
        return;
    }
    try {
      setLoading(true);
      // Ensure axios header is set (might be redundant if handleSetToken was just called, but safe)
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      
      // Fetch user data
      const response = await axios.get(`${API_URL}/auth/me`);
      
      if (response.data && response.data.user) {
        setCurrentUser(response.data.user);
        setError(null);
      } else {
         // Handle case where API returns success but no user data
         console.warn("Fetched user data but response format was unexpected:", response.data);
         handleSetToken(null); // Clear token if user data is invalid
         setCurrentUser(null);
      }
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      // If fetching user fails (e.g., 401 Unauthorized), log out
      handleSetToken(null);
      setCurrentUser(null);
      setError('Session expired or invalid. Please log in again.');
    } finally {
      setLoading(false);
    }
  }, [handleSetToken]); // Dependency on handleSetToken

  // Check if user is logged in on component mount or when token changes externally
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
        // If state token doesn't match stored token, update state and fetch user
        if (storedToken !== token) {
            handleSetToken(storedToken);
            fetchCurrentUser(storedToken);
        } else if (!currentUser) { // If token matches but user not loaded, fetch user
            fetchCurrentUser(storedToken);
        }
    } else {
        // No token found
        if (token) handleSetToken(null); // Clear state if needed
        setCurrentUser(null);
        setLoading(false);
    }
    // Intentionally excluding currentUser from dependencies to avoid loops on user update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, handleSetToken, fetchCurrentUser]); 

  // Register new user
  const register = async (email, password, fullName, personType, companyName) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password,
        fullName,
        personType,
        companyName
      }, {
        headers: {
          'X-Entry-Password': process.env.REACT_APP_ENTRY_GATE_PASSWORD || undefined
        }
      });
      
      return {
        success: true,
        message: response.data.message || 'Registration successful. Please check your email to verify your account.'
      };
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      if (response.data && response.data.token) {
        const { token: newToken, user } = response.data;
        handleSetToken(newToken); // Use centralized function to set token
        setCurrentUser(user);
        return { success: true };
      } else {
        throw new Error('Invalid response from server during login');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      handleSetToken(null); // Clear token on login failure
      setCurrentUser(null);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Login with Google OAuth
  const loginWithGoogle = async (googleData) => {
    try {
      setLoading(true);
      setError(null);
      
      const { credential } = googleData;
      const decodedToken = jwtDecode(credential);
      
      const userData = {
        email: decodedToken.email,
        name: decodedToken.name,
        picture: decodedToken.picture,
        sub: decodedToken.sub
      };
      
      const response = await axios.post(`${API_URL}/auth/google`, {
        token: credential,
        userData
      });
      
      if (response.data && response.data.token) {
        const { token: newToken, user } = response.data;
        handleSetToken(newToken); // Use centralized function
        setCurrentUser(user);
        return { success: true };
      } else {
        throw new Error('Invalid response from server during Google login');
      }
    } catch (err) {
      console.error('Google login error:', err);
      const errorMessage = err.response?.data?.error || 'Google login failed. Please try again.';
      setError(errorMessage);
      handleSetToken(null); // Clear token on failure
      setCurrentUser(null);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Verify email with token
  const verifyEmail = async (verificationToken) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Verifying email with token:', verificationToken);
      const response = await axios.get(`${API_URL}/auth/verify-email?token=${verificationToken}`);
      console.log('Verification response:', response.data);
      
      return {
        success: true,
        message: response.data.message || 'Email verified successfully.'
      };
    } catch (err) {
      console.error('Email verification error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || 'Email verification failed. Please try again.';
      setError(errorMessage);
      
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = useCallback(() => {
    handleSetToken(null); // Use centralized function to clear token
    setCurrentUser(null);
    // Optionally redirect or clear other state here
  }, [handleSetToken]);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!currentUser || !currentUser.roles) return false;
    return currentUser.roles.includes(role);
  }, [currentUser]);

  // Check if user has an active subscription or has free queries remaining
  // NOTE: This logic might need refinement based on your exact subscription model
  const hasActiveSubscription = useCallback(() => {
    if (hasRole('admin')) return true;
    if (currentUser && currentUser.subscription) return true; // Assuming subscription object exists if active
    // if (currentUser && currentUser.queryCount !== undefined) {
    //   return currentUser.queryCount < 3; // Example free tier logic
    // }
    return false; // Default to false if no admin/subscription/free tier
  }, [currentUser, hasRole]);

  const getSubscriptionDetails = useCallback(() => {
    if (!currentUser) return { type: 'none', isActive: false }; // Handle no user case
    if (hasRole('admin')) return { type: 'admin', isActive: true };
    if (!currentUser.subscription) {
      return {
        type: 'free', // Or 'none' depending on your model
        // limit: 3, // Example free tier limit
        isActive: false
      };
    }
    
    const subscription = currentUser.subscription;
    // Assuming subscription object contains plan details
    return {
      type: subscription.plan_name || 'unknown', // Adjust based on your user object structure
      limit: subscription.features?.chat_limit, // Example feature access
      isActive: subscription.is_active !== undefined ? subscription.is_active : true, // Default to true if object exists
      expiresAt: subscription.end_date, // Adjust field name
      features: subscription.features || {}
    };
  }, [currentUser, hasRole]);

  // Function to manually refresh user data (e.g., after subscription update)
  const refreshUserSubscription = useCallback(async () => {
      if (token) {
          await fetchCurrentUser(token);
      }
  }, [token, fetchCurrentUser]);

  // Context value - NOW INCLUDES THE TOKEN
  const value = {
    currentUser,
    token, // <-- EXPOSED TOKEN
    loading,
    error,
    register,
    login,
    loginWithGoogle,
    verifyEmail,
    logout,
    // updateUser, // Removed as fetchCurrentUser handles updates based on token
    hasRole,
    hasActiveSubscription,
    getSubscriptionDetails,
    refreshUserSubscription // Expose refresh function
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
